import React, { useState } from 'react';
import PropTypes from 'prop-types';

const PantryItemList = ({ items, selectedItems, onToggleSelect, onEdit, onDelete, onIncrementQuantity }) => {
  const [isMobile, setIsMobile] = useState(false);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const dateObj = date instanceof Date ? date : date.toDate?.() || new Date(date);
    return dateObj.toLocaleDateString();
  };

  if (items.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-8 text-center">
        <p className="text-gray-500">No items in your pantry yet. Add your first item!</p>
      </div>
    );
  }

  // Mobile card view
  if (isMobile) {
    return (
      <div className="space-y-2">
        {/* Select All */}
        <div className="bg-white rounded-lg shadow-sm p-2 flex items-center justify-between">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={items.length > 0 && items.every(item => selectedItems.has(item.id))}
              onChange={(e) => {
                if (e.target.checked) {
                  items.forEach(item => {
                    if (!selectedItems.has(item.id)) {
                      onToggleSelect(item.id);
                    }
                  });
                } else {
                  items.forEach(item => {
                    if (selectedItems.has(item.id)) {
                      onToggleSelect(item.id);
                    }
                  });
                }
              }}
              className="w-5 h-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
            />
            <span className="text-sm font-medium text-gray-700">Select All</span>
          </label>
        </div>

        {/* Items as cards */}
        {items.map((item) => {
          const isZeroQuantity = (item.quantity || 0) === 0;
          return (
          <div
            key={item.id}
            className={`rounded-lg shadow-sm p-2.5 border-l-4 ${
              isZeroQuantity
                ? 'bg-red-50 border-red-400'
                : 'bg-white border-purple-500'
            }`}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center space-x-2 flex-1 min-w-0">
                <input
                  type="checkbox"
                  checked={selectedItems.has(item.id)}
                  onChange={() => onToggleSelect(item.id)}
                  className="w-5 h-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500 flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-gray-900 truncate">{item.name}</h3>
                    <span
                      className={`px-1.5 py-0.5 text-xs font-semibold rounded-full whitespace-nowrap flex-shrink-0 ${
                        item.location === 'refrigerator'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-orange-100 text-orange-800'
                      }`}
                    >
                      {item.location === 'refrigerator' ? '❄️' : '📦'}
                    </span>
                  </div>
                  {item.category && (
                    <p className="text-xs text-gray-500 truncate">{item.category}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-1 flex-shrink-0">
                <button
                  onClick={() => onEdit(item)}
                  className="p-1.5 text-purple-600 hover:bg-purple-100 rounded active:bg-purple-200"
                  aria-label="Edit item"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                <button
                  onClick={() => onDelete(item.id)}
                  className="p-1.5 text-red-600 hover:bg-red-100 rounded active:bg-red-200"
                  aria-label="Delete item"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between text-xs mt-2 pt-2 border-t border-gray-100 gap-2">
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-gray-600 whitespace-nowrap select-none">
                  Qty: <span className="font-medium text-gray-900 min-w-[70px] inline-block text-right select-none">{item.quantity} {item.unit || ''}</span>
                </span>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onIncrementQuantity(item.id, -1);
                    }}
                    className="w-8 h-6 flex items-center justify-center bg-red-100 text-red-700 rounded hover:bg-red-200 active:bg-red-300 font-bold text-sm flex-shrink-0 select-none"
                    aria-label="Decrease quantity"
                  >
                    −
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onIncrementQuantity(item.id, 1);
                    }}
                    className="w-8 h-6 flex items-center justify-center bg-green-100 text-green-700 rounded hover:bg-green-200 active:bg-green-300 font-bold text-sm flex-shrink-0 select-none"
                    aria-label="Increase quantity"
                  >
                    +
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-gray-600 whitespace-nowrap">
                  Price: <span className="font-medium text-gray-900">{formatCurrency(item.price)}</span>
                </span>
                {item.expirationDate && (
                  <span className="text-gray-600 whitespace-nowrap">
                    Exp: <span className="font-medium text-gray-900">{formatDate(item.expirationDate)}</span>
                  </span>
                )}
              </div>
            </div>
          </div>
          );
        })}
      </div>
    );
  }

  // Desktop table view
  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                <input
                  type="checkbox"
                  checked={items.length > 0 && items.every(item => selectedItems.has(item.id))}
                  onChange={(e) => {
                    if (e.target.checked) {
                      items.forEach(item => {
                        if (!selectedItems.has(item.id)) {
                          onToggleSelect(item.id);
                        }
                      });
                    } else {
                      items.forEach(item => {
                        if (selectedItems.has(item.id)) {
                          onToggleSelect(item.id);
                        }
                      });
                    }
                  }}
                  className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[200px] max-w-[200px]">
                Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[180px]">
                Quantity
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[140px]">
                Location
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[100px]">
                Price
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[120px]">
                Expiration
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[120px]">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {items.map((item) => {
              const isZeroQuantity = (item.quantity || 0) === 0;
              return (
              <tr 
                key={item.id} 
                className={`hover:bg-gray-50 ${
                  isZeroQuantity ? 'bg-red-50' : ''
                }`}
              >
                <td className="px-4 py-3 whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={selectedItems.has(item.id)}
                    onChange={() => onToggleSelect(item.id)}
                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                </td>
                <td className="px-4 py-3 w-[200px] max-w-[200px]">
                  <div className="text-sm font-medium text-gray-900 truncate">{item.name}</div>
                  {item.category && (
                    <div className="text-sm text-gray-500 truncate">{item.category}</div>
                  )}
                </td>
                <td className="px-4 py-3 whitespace-nowrap w-[180px]">
                  <div className="flex items-center justify-end gap-2">
                    <span className="text-sm text-gray-900 text-right select-none truncate" style={{ maxWidth: '90px' }}>
                      {item.quantity} {item.unit || ''}
                    </span>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => onIncrementQuantity(item.id, -1)}
                        className="w-7 h-7 flex items-center justify-center bg-red-100 text-red-700 rounded hover:bg-red-200 active:bg-red-300 font-bold text-sm transition-colors flex-shrink-0 select-none"
                        aria-label="Decrease quantity"
                        title="Decrease quantity"
                      >
                        −
                      </button>
                      <button
                        onClick={() => onIncrementQuantity(item.id, 1)}
                        className="w-7 h-7 flex items-center justify-center bg-green-100 text-green-700 rounded hover:bg-green-200 active:bg-green-300 font-bold text-sm transition-colors flex-shrink-0 select-none"
                        aria-label="Increase quantity"
                        title="Increase quantity"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span
                    className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      item.location === 'refrigerator'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-orange-100 text-orange-800'
                    }`}
                  >
                    {item.location === 'refrigerator' ? '❄️ Refrigerator' : '📦 Pantry'}
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                  {formatCurrency(item.price)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                  {formatDate(item.expirationDate)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium space-x-2">
                  <button
                    onClick={() => onEdit(item)}
                    className="text-purple-600 hover:text-purple-900"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => onDelete(item.id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    Delete
                  </button>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

PantryItemList.propTypes = {
  items: PropTypes.array.isRequired,
  selectedItems: PropTypes.instanceOf(Set).isRequired,
  onToggleSelect: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onIncrementQuantity: PropTypes.func.isRequired,
};

export default PantryItemList;
